// ================================
// IMPORTS
// ================================
import { inject, ref, useId, type Ref } from "vue"
import { useI18n } from "vue-i18n"
import { useConfirm, useToast } from "primevue"
import type { Services } from "@services"
import type { CompanyUser } from "@core/services/user.service"

// ================================
// TYPES & INTERFACES
// ================================

export type CompanyMemberRole =
    | "administrateur"
    | "employe"
    | "employe_read_only"

/**
 * Retour du composable useCompanyMemberActions
 */
export type UseCompanyMemberActionsReturn = {
    /** ID du groupe pour BaseConfirmDialog (changement de rôle et suppression) */
    componentId: string
    /** Lance le flux de changement de rôle (avec confirmation si démotion vers employe) */
    onChangeRole: (payload: {
        userId: string
        role: CompanyMemberRole
    }) => Promise<void>
    /** Lance le flux de suppression (confirmation puis appel API et mise à jour de la liste) */
    onDeleteUser: (member: CompanyUser) => void
}

// ================================
// COMPOSABLE
// ================================

/**
 * Composable pour la logique métier du changement de rôle et de la suppression
 * d'un membre dans l'espace entreprise.
 * Gère les confirmations (PrimeVue useConfirm), les appels API et la mise à jour
 * optimiste de la liste des membres.
 */
export function useCompanyMemberActions(
    members: Ref<CompanyUser[]>
): UseCompanyMemberActionsReturn {
    const { $updateCompanyUserRole, $deleteCompanyUser } =
        inject<Services>("services")!

    const toast = useToast()
    const confirm = useConfirm()
    const componentId = useId()

    /** Évite d'ouvrir deux fois la modal de confirmation (démotion vers employe) */
    const userIdModal = ref<string | null>(null)

    const { t } = useI18n({
        useScope: "global",
        messages: {
            fr: {
                common: {
                    success: "Succès",
                    error: "Erreur",
                    cancel: "Annuler",
                    confirm: "Confirmer",
                },
                modal_confirm: {
                    title: "Êtes-vous sûr de vouloir changer le rôle de ce membre ?",
                    message:
                        "En changeant le rôle de ce membre, il perdra l'accès à tous les bâtiments (il faudra ensuite lui partager les bâtiments de votre choix).",
                },
                confirm_delete:
                    "Êtes-vous sûr de vouloir supprimer le compte de ce membre ?",
                confirm_delete_message: "Cette action est irréversible.",
                confirm_delete_button: "Supprimer",
                user_deleted: "L'utilisateur a bien été supprimé.",
                role_updated: "Le rôle a été mis à jour avec succès.",
                error_update_role:
                    "Une erreur est survenue lors de la mise à jour du rôle.",
                error_delete_user:
                    "Une erreur est survenue lors de la suppression de l'utilisateur.",
            },
        },
    })

    async function performRoleChange(
        userId: string,
        role: CompanyMemberRole
    ): Promise<void> {
        const oldRole = members.value.find(
            (member) => member._id === userId
        )?.type

        try {
            members.value = members.value.map((member) => {
                if (member._id === userId) {
                    return { ...member, type: role }
                }
                return member
            })

            if (!$updateCompanyUserRole) {
                throw new Error("Service $updateCompanyUserRole non disponible")
            }
            await $updateCompanyUserRole(userId, role)

            toast.add({
                severity: "success",
                summary: t("common.success"),
                detail: t("role_updated"),
                life: 3000,
            })
        } catch (err: unknown) {
            if (oldRole) {
                members.value = members.value.map((member) => {
                    if (member._id === userId) {
                        return {
                            ...member,
                            type: oldRole as CompanyUser["type"],
                        }
                    }
                    return member
                })
            }

            const errorMessage =
                err instanceof Error ? err.message : t("error_update_role")
            toast.add({
                severity: "error",
                summary: t("common.error"),
                detail: errorMessage,
                life: 5000,
            })
        }
    }

    async function onChangeRole({
        userId,
        role,
    }: {
        userId: string
        role: CompanyMemberRole
    }): Promise<void> {
        if (role === "employe" && userIdModal.value === null) {
            userIdModal.value = userId
            confirm.require({
                group: componentId,
                message: t("modal_confirm.message"),
                header: t("modal_confirm.title"),
                icon: "pi pi-exclamation-triangle",
                rejectLabel: t("common.cancel"),
                rejectProps: {
                    label: t("common.cancel"),
                    outlined: true,
                    severity: "secondary",
                },
                acceptProps: {
                    label: t("common.confirm"),
                    severity: "warn",
                },
                accept: async () => {
                    await performRoleChange(userId, role)
                    userIdModal.value = null
                },
                reject: () => {
                    userIdModal.value = null
                },
            })
            return
        }

        await performRoleChange(userId, role)
    }

    async function performDeleteUser(member: CompanyUser): Promise<void> {
        if (!$deleteCompanyUser) {
            toast.add({
                severity: "error",
                summary: t("common.error"),
                detail: t("error_delete_user"),
                life: 5000,
            })
            return
        }

        const index = members.value.findIndex((m) => m._id === member._id)
        const previousMembers = [...members.value]

        // Retrait optimiste : on enlève le membre de la liste tout de suite
        members.value = members.value.filter((m) => m._id !== member._id)

        try {
            await $deleteCompanyUser(member._id, member.type)

            toast.add({
                severity: "success",
                summary: t("common.success"),
                detail: t("user_deleted"),
                life: 3000,
            })
        } catch (err: unknown) {
            // En cas d'erreur : on remet le membre dans la liste (à sa position d'origine)
            if (index >= 0) {
                const restored = [...members.value]
                restored.splice(index, 0, member)
                members.value = restored
            } else {
                members.value = previousMembers
            }

            const errorMessage =
                err instanceof Error ? err.message : t("error_delete_user")
            toast.add({
                severity: "error",
                summary: t("common.error"),
                detail: errorMessage,
                life: 5000,
            })
        }
    }

    function onDeleteUser(member: CompanyUser): void {
        confirm.require({
            group: componentId,
            message: t("confirm_delete_message"),
            header: t("confirm_delete"),
            icon: "pi pi-exclamation-triangle",
            rejectLabel: t("common.cancel"),
            rejectProps: {
                label: t("common.cancel"),
                outlined: true,
                severity: "secondary",
            },
            acceptProps: {
                label: t("confirm_delete_button"),
                severity: "danger",
                icon: "pi pi-trash",
            },
            accept: async () => {
                await performDeleteUser(member)
            },
        })
    }

    return {
        componentId,
        onChangeRole,
        onDeleteUser,
    }
}
